import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { AbstractBlModal, ModalSize, BlDialogService, ModalReason, BlNumberInputComponent } from '@blcloud/bl-ng-ui-component';
import { IContactStatusBgColorSetting } from '@blcloud/bl-datamodel';
import { cloneDeep as _cloneDeep, delay as _delay } from 'lodash';
import { ContactBgColorSettingModalService } from './contact-bg-color-setting-modal.service';
import { finalize } from 'rxjs/operators';

/**
 * コンタクト状況背景色設定コンポーネント
 */
@Component({
  templateUrl: './contact-bg-color-setting-modal.component.html',
  styleUrls: ['./contact-bg-color-setting-modal.component.scss'],
  providers: [ContactBgColorSettingModalService]
})
export class ContactBgColorSettingModalComponent extends AbstractBlModal implements OnInit {

  /** 画面タイトル */
  headerTitle = 'コンタクト状況別背景色設定';
  /** 呼び元画面に返すための背景色設定 */
  result: IContactStatusBgColorSetting;
  /** 変更前の背景色設定 */
  beforeData: IContactStatusBgColorSetting;

  // --------------------------------------------------
  // Life Cycle

  /**
   * コンストラクタ
   */
  constructor(
    private service: ContactBgColorSettingModalService,
    private dialogService: BlDialogService,
    private changeDetectorRef: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit() {
    this.showLoading();
    // 初期表示設定取得
    this.service.getContactStatusBgColorSetting().pipe(finalize(() => this.hideLoading())).subscribe(
      setting => {
        this.result = setting;
        this.beforeData = _cloneDeep(setting);
      }
    );
  }

  // --------------------------------------------------
  // AbstractModal Override

  /**
   * @override
   */
  getResults() {
    return this.result;
  }

  /**
   * @override
   */
  defaultOption() {
    return {
      size: ModalSize.Large,
    };
  }

  /**
   * モーダル終了イベント
   * @param reason　モーダル終了理由
   */
  dismiss(reason: ModalReason) {
    if (reason === ModalReason.Done) {
      this.dialogService.confirm('コンタクト背景色設定を変更します。よろしいですか？').subscribe(
        modalRef => {
          if (modalRef.reason === ModalReason.Done) {
            this.emitDismissEvent(reason);
          }
          modalRef.hide();
        }
      );
    } else {
      this.result = this.beforeData;
      this.emitDismissEvent(reason);
    }
  }

  // --------------------------------------------------
  // 内部処理

  /**
   * 実施状況ステータス1の変更イベント
   * @param value 入力値
   * @param input 入力コンポーネント
   */
  onChangeImplementationStatus1(value: number, input: BlNumberInputComponent) {
    if (value > 0 && value !== this.result.contactImplementationStatus.contactStatus2 &&
      value !== this.result.contactImplementationStatus.contactStatus3) {
      this.beforeData.contactImplementationStatus.contactStatus1 = this.result.contactImplementationStatus.contactStatus1;
    } else {
      _delay(() => {
        this.result.contactImplementationStatus.contactStatus1 = this.beforeData.contactImplementationStatus.contactStatus1;
        this.changeDetectorRef.detectChanges();
      }, 100);

      if (value !== 0) {
        this.showEquivalentWarnMessage(input);
      }
    }
  }

  /**
   * 実施状況ステータス2の変更イベント
   * @param value 入力値
   * @param input 入力コンポーネント
   */
  onChangeImplementationStatus2(value: number, input: BlNumberInputComponent) {
    if (value > 0 && value !== this.result.contactImplementationStatus.contactStatus1 &&
      value !== this.result.contactImplementationStatus.contactStatus3) {
      this.beforeData.contactImplementationStatus.contactStatus2 = this.result.contactImplementationStatus.contactStatus2;
    } else {
      _delay(() => {
        this.result.contactImplementationStatus.contactStatus2 = this.beforeData.contactImplementationStatus.contactStatus2;
        this.changeDetectorRef.detectChanges();
      }, 100);

      if (value !== 0) {
        this.showEquivalentWarnMessage(input);
      }
    }
  }

  /**
   * 実施状況ステータス3の変更イベント
   * @param value 入力値
   * @param input 入力コンポーネント
   */
  onChangeImplementationStatus3(value: number, input: BlNumberInputComponent) {
    if (value > 0 && value !== this.result.contactImplementationStatus.contactStatus1 &&
      value !== this.result.contactImplementationStatus.contactStatus2) {
      this.beforeData.contactImplementationStatus.contactStatus3 = this.result.contactImplementationStatus.contactStatus3;
    } else {
      _delay(() => {
        this.result.contactImplementationStatus.contactStatus3 = this.beforeData.contactImplementationStatus.contactStatus3;
        this.changeDetectorRef.detectChanges();
      }, 100);

      if (value !== 0) {
        this.showEquivalentWarnMessage(input);
      }
    }
  }

  /**
   * 成約状況ステータス1の変更イベント
   * @param value 入力値
   * @param input 入力コンポーネント
   */
  onChangeContractStatus1(value: number, input: BlNumberInputComponent) {
    if (value > 0 && value !== this.result.contactContractStatus.contactStatus2 &&
      value !== this.result.contactContractStatus.contactStatus3) {
      this.beforeData.contactContractStatus.contactStatus1 = this.result.contactContractStatus.contactStatus1;
    } else {
      _delay(() => {
        this.result.contactContractStatus.contactStatus1 = this.beforeData.contactContractStatus.contactStatus1;
        this.changeDetectorRef.detectChanges();
      }, 100);

      if (value !== 0) {
        this.showEquivalentWarnMessage(input);
      }
    }
  }

  /**
   * 成約状況ステータス2の変更イベント
   * @param value 入力値
   * @param input 入力コンポーネント
   */
  onChangeContractStatus2(value: number, input: BlNumberInputComponent) {
    if (value > 0 && value !== this.result.contactContractStatus.contactStatus1 &&
      value !== this.result.contactContractStatus.contactStatus3) {
      this.beforeData.contactContractStatus.contactStatus2 = this.result.contactContractStatus.contactStatus2;
    } else {
      _delay(() => {
        this.result.contactContractStatus.contactStatus2 = this.beforeData.contactContractStatus.contactStatus2;
        this.changeDetectorRef.detectChanges();
      }, 100);

      if (value !== 0) {
        this.showEquivalentWarnMessage(input);
      }
    }
  }

  /**
   * 成約状況ステータス3の変更イベント
   * @param value 入力値
   * @param input 入力コンポーネント
   */
  onChangeContractStatus3(value: number, input: BlNumberInputComponent) {
    if (value > 0 && value !== this.result.contactContractStatus.contactStatus1 &&
      value !== this.result.contactContractStatus.contactStatus2) {
      this.beforeData.contactContractStatus.contactStatus3 = this.result.contactContractStatus.contactStatus3;
    } else {
      _delay(() => {
        this.result.contactContractStatus.contactStatus3 = this.beforeData.contactContractStatus.contactStatus3;
        this.changeDetectorRef.detectChanges();
      }, 100);

      if (value !== 0) {
        this.showEquivalentWarnMessage(input);
      }
    }
  }

  /**
   * 同値警告メッセージを表示します。
   * @param input 入力コンポーネント
   */
  private showEquivalentWarnMessage(input: BlNumberInputComponent) {
    this.dialogService.warn('同じ数値を入力しないで下さい。').subscribe(
      modalRef => {
        modalRef.hide();
        input.focus();
      }
    );
  }
}



// WEBPACK FOOTER //
// ./src/app/feature/contact-bg-color-setting/contact-bg-color-setting-modal.component.ts